import axios from "axios";
import { User } from "@/models/User";
import { Permission } from "@/models/Permission";
import Vue from "vue";
import { computeBackoffDelay } from "./authShared";

export interface UserInfoResponse {
    user: User;
    permissions: Permission[];
    pageNames?: Record<string, string>;
    remoteDisabledStates?: string[];
    stateRestrictedStates?: string[];
}

export enum UserInfoError {
    NETWORK_ERROR = 'NETWORK_ERROR',
    AUTH_ERROR = 'AUTH_ERROR',
    NO_USER_DATA = 'NO_USER_DATA',
    UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export class UserInfoServiceError extends Error {
    constructor(
        public readonly type: UserInfoError,
        message: string,
        public readonly originalError?: any
    ) {
        super(message);
        this.name = 'UserInfoServiceError';
    }
}

export interface UserInfoServiceOptions {
    forceFresh?: boolean;
    // Loaded data older than this is refetched; defaults to the cache duration.
    maxAgeMs?: number;
}

class UserInfoService {
    private fetchPromise: Promise<UserInfoResponse> | null = null;
    private lastFetchTime = 0;
    // Permission changes arrive as a PermissionsChanged push, so while the hub is connected this
    // is only the backstop for a change the server never announces.
    private readonly CACHE_DURATION = 15 * 60_000;
    // Without a connected hub no push arrives and nothing marks the data stale, so data loaded
    // then is refetched on the first navigation after this.
    private readonly PUSH_DOWN_CACHE_DURATION = 30_000;
    // When the hub last connected; null while it is down. Only data loaded since then is covered
    // by the push, since one sent before the connection was never received.
    private pushLiveSince: number | null = null;

    // Every fetch is numbered. A response is written only if no later-issued response has been
    // written already: a forceFresh fetch overlaps one in flight, and an older response landing
    // after it would restore the permissions the push replaced and stamp them fresh. An older one
    // that lands while the newer is still out, or after it failed, still fills the cache.
    private issued = 0;
    private applied = 0;
    // A forceFresh fetch or markStale means a request issued before it may have read permissions
    // that have since changed. Its response still fills the cache but is never counted fresh, so
    // the next navigation refetches even if the newer request fails.
    private staleThrough = 0;
    // Set by clearUserData, so a fetch in flight at sign-out is discarded instead of resurrecting
    // the previous user.
    private sessionFloor = 0;
    
    // Exponential backoff for failures
    private failureCount = 0;
    private lastFailureTime = 0;
    private readonly backoffConfig = {
        baseDelay: 1000,      // Start with 1 second
        maxDelay: 60000,      // Max 1 minute
        jitterPercent: 0.3,   // 30% jitter
        maxFailures: 5        // Max consecutive failures before long backoff
    };

    async fetchUserInfoIfNeeded(excludeUrl?: string): Promise<UserInfoResponse | null> {
        if (!this.shouldFetchUserInfo(excludeUrl)) {
            return Promise.resolve(null);
        }
        
        // Check if we're in a backoff period
        if (this.isInBackoffPeriod()) {
            // Return cached data if available during backoff
            if (this.hasUserData()) {
                return this.getCurrentUserData();
            }
            return Promise.resolve(null);
        }
        
        return this.fetchUserInfo();
    }

    /**
     * For the route guard: waits only when no user is loaded. Loaded data is returned at once,
     * and once it is past the cache duration a refresh starts behind the navigation.
     */
    async getUserInfo(): Promise<UserInfoResponse> {
        if (!this.hasUserData()) {
            return this.fetchUserInfo();
        }

        // Not awaited; a fetch already in flight is joined rather than repeated.
        if (!this.hasRecentUserData() && !this.isInBackoffPeriod()) {
            this.fetchUserInfo().catch(error => {
                console.warn("Background user info refresh failed:", error);
            });
        }

        return this.getCurrentUserData();
    }

    /**
     * Refetches when the loaded data is older than maxAgeMs, so a denial is never decided on a
     * stale permission list. A failed refetch leaves the cached data in place.
     */
    async refreshIfOlderThan(maxAgeMs: number): Promise<void> {
        if (this.isInBackoffPeriod()) return;

        await this.fetchUserInfo({ maxAgeMs }).catch(error => {
            console.warn("User info recheck failed:", error);
        });
    }

    /**
     * For a reconnect: a PermissionsChanged push sent while the hub was down is lost for good.
     * Marking the data stale instead of refetching lets each tab refresh on its next navigation
     * rather than every tab of every user fetching at once when a deploy drops the hub.
     */
    markStale(): void {
        this.staleThrough = this.issued;
        this.lastFetchTime = 0;
    }

    /** Reports whether the hub that delivers PermissionsChanged is connected. */
    setPushLive(live: boolean): void {
        if (!live) {
            this.pushLiveSince = null;
        } else if (this.pushLiveSince === null) {
            this.pushLiveSince = Date.now();
        }
    }

    async fetchUserInfo(options: UserInfoServiceOptions = {}): Promise<UserInfoResponse> {
        const { forceFresh = false, maxAgeMs } = options;

        if (this.fetchPromise && !forceFresh) {
            return this.fetchPromise;
        }

        if (!forceFresh && this.hasRecentUserData(maxAgeMs)) {
            return this.getCurrentUserData();
        }

        // The cached data is stale too, so a failed forced fetch doesn't leave it counted fresh.
        if (forceFresh) {
            this.markStale();
        }

        const generation = ++this.issued;
        const request = this.performFetch(generation);
        this.fetchPromise = request;

        try {
            return await request;
        } catch (error) {
            // Track failure for exponential backoff, unless a newer request has already succeeded.
            if (this.isNewerThanApplied(generation)) {
                this.failureCount++;
                this.lastFailureTime = Date.now();
            }
            throw error;
        } finally {
            // A newer fetch may have replaced this one; clearing it would stop callers joining it.
            if (this.fetchPromise === request) {
                this.fetchPromise = null;
            }
        }
    }

    private async performFetch(generation: number): Promise<UserInfoResponse> {
        try {
            console.log("Fetching user information...");

            const endpoint = "api/user/info";
            // The data's age runs from when the server could have read it, not from when it
            // arrived, so a request sent before the hub connected isn't covered by the push.
            const sentAt = Date.now();

            const response = await axios.get<{
                user: User;
                permissions: Permission[];
                pageNames?: Record<string, string>;
                remoteDisabledStates?: string[];
                stateRestrictedStates?: string[];
            }>(endpoint);

            const data = response.data;

            if (!data.user?.userName) {
                throw new UserInfoServiceError(
                    UserInfoError.NO_USER_DATA,
                    "No user information available in response"
                );
            }

            // Create properly typed objects
            const user = new User(data.user);

            const permissions = data.permissions.map(p => new Permission(p));
            const pageNames = data.pageNames || {};
            const remoteDisabledStates = data.remoteDisabledStates ?? [];
            const stateRestrictedStates = data.stateRestrictedStates ?? [];

            // Update Vue prototypes, unless a newer response or a sign-out superseded this one.
            if (this.isNewerThanApplied(generation)) {
                this.assignIfChanged("$user", user);
                this.assignIfChanged("$permissions", permissions);
                this.assignIfChanged("$remoteDisabledStates", remoteDisabledStates);
                this.assignIfChanged("$stateRestrictedStates", stateRestrictedStates);
                if (data.pageNames) {
                    this.assignIfChanged("$pageNames", pageNames);
                }

                this.applied = generation;
                this.lastFetchTime = generation > this.staleThrough ? sentAt : 0;
                this.failureCount = 0;
                this.lastFailureTime = 0;
            }

            console.log("User information fetched successfully");

            return {
                user,
                permissions,
                pageNames,
                remoteDisabledStates,
                stateRestrictedStates
            };

        } catch (error) {
            console.error("Error fetching user info:", error);

            const err = error as any;

            // Classify the error type for better handling
            if (error instanceof UserInfoServiceError) {
                throw error;
            }

            if (err?.response?.status === 401) {
                throw new UserInfoServiceError(
                    UserInfoError.AUTH_ERROR,
                    "Authentication required",
                    error
                );
            }

            if (err?.code === 'NETWORK_ERROR' || !navigator.onLine) {
                throw new UserInfoServiceError(
                    UserInfoError.NETWORK_ERROR,
                    "Network connectivity issue",
                    error
                );
            }

            throw new UserInfoServiceError(
                UserInfoError.UNKNOWN_ERROR,
                `Failed to fetch user info: ${err.message || 'Unknown error'}`,
                error
            );
        }
    }

    // Writing an equal copy still notifies every reader -- a new $pageNames rebuilds the whole
    // sidebar -- so a refresh that returns the same data leaves the current object in place.
    private assignIfChanged(key: string, value: unknown): void {
        if (JSON.stringify(Vue.prototype[key]) === JSON.stringify(value)) return;
        Vue.prototype[key] = value;
    }

    private cacheDuration(): number {
        const pushCoversData = this.pushLiveSince !== null && this.lastFetchTime >= this.pushLiveSince;
        return pushCoversData ? this.CACHE_DURATION : this.PUSH_DOWN_CACHE_DURATION;
    }

    private isNewerThanApplied(generation: number): boolean {
        return generation > this.applied && generation > this.sessionFloor;
    }

    private hasRecentUserData(maxAgeMs = this.cacheDuration()): boolean {
        return this.hasUserData() && Date.now() - this.lastFetchTime < maxAgeMs;
    }

    private hasUserData(): boolean {
        return !!(Vue.prototype.$user?.userName && Vue.prototype.$permissions?.length);
    }

    private isInBackoffPeriod(): boolean {
        if (this.failureCount === 0) return false;

        const delayWithJitter = computeBackoffDelay(this.failureCount - 1, this.backoffConfig);
        const timeSinceFailure = Date.now() - this.lastFailureTime;

        return timeSinceFailure < delayWithJitter;
    }

    private getCurrentUserData(): UserInfoResponse {
        return {
            user: Vue.prototype.$user,
            permissions: Vue.prototype.$permissions,
            pageNames: Vue.prototype.$pageNames,
            remoteDisabledStates: Vue.prototype.$remoteDisabledStates,
            stateRestrictedStates: Vue.prototype.$stateRestrictedStates
        };
    }

    /**
     * Check if user info should be fetched based on current state
     */
    private shouldFetchUserInfo(excludeUrl?: string): boolean {
        // Don't fetch if we're already fetching
        if (this.fetchPromise) {
            return false;
        }

        // Don't fetch if we're calling the user info endpoint itself
        if (excludeUrl?.toLowerCase().includes("user/info")) {
            return false;
        }

        // Fetch if we don't have user data
        return !this.hasUserData();
    }

    /**
     * Clear cached user data (useful for logout scenarios)
     */
    clearUserData(): void {
        this.sessionFloor = ++this.issued;
        Vue.prototype.$user = new User();
        Vue.prototype.$permissions = [];
        Vue.prototype.$pageNames = {};
        Vue.prototype.$remoteDisabledStates = [];
        Vue.prototype.$stateRestrictedStates = [];
        this.lastFetchTime = 0;
        
        // Reset failure tracking
        this.failureCount = 0;
        this.lastFailureTime = 0;
    }
}

// Export singleton instance
export const userInfoService = new UserInfoService();
export default userInfoService;